import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Handshake, Loader2, ArrowRight, AlertCircle } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export default function CoListAcceptInvite() {
    const { token } = useParams();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [invite, setInvite] = useState(null);
    const [loading, setLoading] = useState(true);
    const [accepting, setAccepting] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!user) {
            navigate(`/login?next=/co-list/accept/${token}`);
            return;
        }
        (async () => {
            try {
                const res = await api.get(`/co-list/invites/${token}`);
                setInvite(res.data);
            } catch (err) {
                setError(formatApiError(err.response?.data?.detail) || "Invite not found");
            } finally {
                setLoading(false);
            }
        })();
    }, [user, token, navigate]);

    const accept = async () => {
        setAccepting(true);
        try {
            const { data } = await api.post(`/co-list/invites/${token}/accept`);
            toast.success(`Locked in. You get ${data.split_pct}% when this closes.`);
            navigate(`/listings/${data.listing_id}`);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not accept");
        } finally {
            setAccepting(false);
        }
    };

    if (!user || loading) {
        return (
            <div className="min-h-[60vh] grid place-items-center">
                <Loader2 className="h-8 w-8 animate-spin text-coral" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-[50vh] grid place-items-center text-center px-4" data-testid="co-list-error-state">
                <Card className="max-w-md w-full p-6 bg-card">
                    <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-3" />
                    <h1 className="font-display text-xl font-bold text-foreground mb-2">Invite not available</h1>
                    <p className="font-app text-sm text-muted-foreground mb-4">{error}</p>
                    <Button asChild className="bg-coral hover:bg-coral-deep text-white">
                        <Link to="/agent-console">Back to console</Link>
                    </Button>
                </Card>
            </div>
        );
    }

    const isMyInvite =
        (user.email || "").toLowerCase().trim() === (invite.co_agent_email || "").toLowerCase().trim();

    return (
        <div className="min-h-[60vh] grid place-items-center px-4 py-12" data-testid="co-list-accept-page">
            <Card className="max-w-md w-full p-6 lg:p-8 bg-card">
                <Handshake className="h-12 w-12 text-coral mx-auto mb-3" />
                <h1 className="font-display text-2xl font-bold text-foreground mb-2 text-center">
                    Co-listing invitation
                </h1>
                <p className="font-app text-sm text-muted-foreground mb-5 text-center">
                    <strong className="text-foreground">{invite.primary_agent_name || "An agent"}</strong> wants to share commission with you on this listing.
                </p>

                <div className="p-4 rounded-md bg-secondary/40 dark:bg-card/60 border border-border mb-5">
                    <div className="font-app text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Listing</div>
                    <div className="font-display text-base font-bold text-foreground mb-3">{invite.listing_title}</div>
                    <div className="flex items-center justify-between">
                        <span className="font-app text-xs text-muted-foreground">Your commission split</span>
                        <Badge className="bg-coral text-white text-sm tabular-nums">{invite.split_pct}%</Badge>
                    </div>
                </div>

                {!isMyInvite ? (
                    <div className="text-center" data-testid="co-list-email-mismatch">
                        <AlertCircle className="h-6 w-6 text-destructive mx-auto mb-2" />
                        <p className="font-app text-sm text-destructive font-semibold mb-1">Email mismatch</p>
                        <p className="font-app text-xs text-muted-foreground">
                            This invite was issued for <code className="px-1 py-0.5 rounded bg-muted font-mono">{invite.co_agent_email}</code> but you're logged in as <code className="px-1 py-0.5 rounded bg-muted font-mono">{user.email}</code>.
                        </p>
                    </div>
                ) : (
                    <>
                        <Button
                            onClick={accept}
                            disabled={accepting}
                            data-testid="co-list-accept-btn"
                            className="w-full bg-coral hover:bg-coral-deep text-white font-app"
                        >
                            {accepting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                            Accept co-listing <ArrowRight className="h-4 w-4 ml-2" />
                        </Button>
                        <p className="font-app text-[10px] text-muted-foreground text-center mt-3">
                            You'll receive your {invite.split_pct}% automatically when the deal is financially closed.
                            No further action needed.
                        </p>
                    </>
                )}
            </Card>
        </div>
    );
}
